import { useEffect } from 'react';
import { useCEProductSelector } from '../../bitf-customization-engine/useCEProductSelector';
import { useCEProductCustomization } from '../../bitf-customization-engine/useCEProductCustomization';
import { useTranslate } from '../../hooks/useTranslate';
import CustomizationButton from '../../components/CustomizationButton';

export default function ProductSelector({
  productSelectorHook,
  localization,
  storeName = 'quick-buy',
  sizeChartUrl,
  primaryLocale,
}) {
  const {
    filteredFilters,
    selectedCollection,
    selectedProduct,
    selectedVariant,
    selectedFilters,
    setFilterValue,
    setVariant,
  } = productSelectorHook || useCEProductSelector({ localization });
  const { translate } = useTranslate(localization?.locale);
  const { setStore } = useCEProductCustomization({
    storeName: storeName,
    watchStoreChanges: false,
    localization,
  });

  const sizeLabelList = 'size_id,size,taille,größe,tamanho,tamaño,koko,サイズ,taglia,size_id'.split(',');

  useEffect(() => {
    if (selectedProduct) {
      setStore((store) => {
        if (store.currentProduct?.id === selectedProduct.id) {
          return;
        }
        store.currentProduct = selectedProduct;
      });

      // Mantieni la taglia precedentemente selezionata, se possibile (case-insensitive sui nomi opzione)
      const prevSize = selectedVariant?.selectedOptions?.find((opt) => {
        const optName = (opt?.name || '').toLowerCase();
        return sizeLabelList.includes(optName);
      })?.value;

      // Trova una variante con la stessa taglia nel nuovo prodotto
      let nextVariant = selectedProduct?.variants?.find((variant) => {
        if (!prevSize) return false;
        return variant?.selectedOptions?.some((opt) => {
          const optName = (opt?.name || '').toLowerCase();
          return sizeLabelList.includes(optName) && opt.value === prevSize;
        });
      });

      // Se la variante con la stessa taglia non è disponibile, prendi la prima disponibile
      if (!nextVariant || !nextVariant.availableForSale) {
        nextVariant = selectedProduct?.variants.find((v) => v.availableForSale) || selectedProduct?.variants?.[0];
      }

      if (nextVariant) {
        setVariant(nextVariant);
        setStore((store) => {
          store.currentProductVariant = nextVariant;
        });
      }
    }
  }, [selectedProduct, storeName, setStore]);

  return (
    <>
      {/* Filters */}
      {selectedCollection &&
        filteredFilters &&
        filteredFilters.map((filter) => (
          <div key={filter.id} className="product-customization__form-section-container">
            <div className="product-customization__form-section-title">
              <p className="Body Medium Primary Uppercase">{filter.label}</p>
            </div>
            <div className="product-customization__form-buttons">
              {filter?.availableValues?.map(({ id, label, disabled }) =>
                !disabled ? (
                  <CustomizationButton
                    key={id}
                    className={`customization-button ${selectedFilters[filter.id] === id ? 'active Medium' : ''}`}
                    onClick={() => setFilterValue(filter.id, id)}
                    disabled={disabled}
                  >
                    <div className="Body--2XS">{label}</div>
                  </CustomizationButton>
                ) : null,
              )}
            </div>
          </div>
        ))}
      {/* Product Options (Variants) */}
      {selectedProduct &&
        (() => {
          const optionNames = Array.from(
            new Set(selectedProduct?.variants.flatMap((variant) => variant.selectedOptions.map((opt) => opt.name))),
          );

          const optionsByName = {};
          selectedProduct?.variants.forEach((variant) => {
            variant?.selectedOptions.forEach((opt) => {
              if (!optionsByName[opt.name]) {
                optionsByName[opt.name] = new Set();
              }
              optionsByName[opt.name].add(opt.value);
            });
          });

          return optionNames.map((optionName) => {
            const values = Array.from(optionsByName[optionName]);
            return (
              <div key={optionName} className="product-customization__form-section-container">
                <div className="product-customization__form-section-title">
                  <p className="Body Medium Primary Uppercase">
                    {optionName.toLowerCase() === 'size_id' ? translate('customization.form.size') : optionName}
                  </p>
                  {sizeLabelList.includes(optionName.toLowerCase()) ? (
                    <a
                      className="product-customization__form-size-chart-link"
                      href={`${primaryLocale ? '' : '/' + localization?.locale}/pages/${sizeChartUrl}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {translate('customization.form.size_chart')}
                    </a>
                  ) : null}
                </div>
                <div className="product-customization__form-buttons product-customization__form-buttons-variant">
                  {values.map((value) => {
                    const matchingVariant = selectedProduct.variants.find((variant) => {
                      const hasCurrentValue = variant?.selectedOptions.some(
                        (opt) => opt.name === optionName && opt.value === value,
                      );

                      if (!hasCurrentValue) return false;
                      if (!selectedVariant) return true;

                      return variant?.selectedOptions.every((opt) => {
                        if (opt.name === optionName) {
                          return true;
                        }
                        const selectedOpt = selectedVariant?.selectedOptions?.find((so) => so.name === opt.name);
                        return !selectedOpt || selectedOpt.value === opt.value;
                      });
                    });

                    const isAvailable = Boolean(matchingVariant?.availableForSale);

                    const isSelected = selectedVariant?.selectedOptions.some(
                      (opt) => opt.name === optionName && opt.value === value,
                    );

                    const isActive = isSelected && isAvailable;

                    return (
                      <CustomizationButton
                        key={value}
                        className={`customization-button ${isActive ? 'active Medium' : ''}`}
                        onClick={() => {
                          if (matchingVariant) {
                            setVariant(matchingVariant);
                            setStore((store) => {
                              store.currentProductVariant = matchingVariant;
                            });
                          }
                        }}
                        disabled={!isAvailable}
                      >
                        <div>{value}</div>
                      </CustomizationButton>
                    );
                  })}
                </div>
              </div>
            );
          });
        })()}
    </>
  );
}
